import {
  Box,
  Flex,
  IconButton,
  Spinner,
  Stack,
  SystemStyleObject,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  Column,
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  OnChangeFn,
  Row,
  SortingState,
  TableMeta,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { throttle } from 'lodash';
import React, { CSSProperties, Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { LOADING_DELAY_THRESHOLD } from '@/apis/constants';
import { SortDownArrowIcon } from '@/components/common/Icons/SortDownArrowIcon';
import { SortUpArrowIcon } from '@/components/common/Icons/SortUpArrowIcon';
import { SortUpDownArrowIcon } from '@/components/common/Icons/SortUpDownArrowIcon';
import { useNextTranslation } from '@/i18n/client';
import { Order } from '@/typings/common';

import TablePagination from '../DataTable/TablePagination';
import { Pagination } from '../DataTable/typings';
import styles from '../table.module.scss';
import IndeterminateCheckbox from '../TableCheckbox';
import { RowSelection } from '../typings';

export interface SortedState<T = string> {
  order?: Order;
  orderBy?: T;
}

const BUFFER_PIXEL = 15; // this buffer is for better user experience when scrolling

// Utility function to find the nearest parent table element
const findNearestParentTable = (element: HTMLElement | null): HTMLTableElement | null => {
  if (!element) {
    return null;
  }

  let currentElement = element.parentElement;
  while (currentElement) {
    if (currentElement.tagName.toLowerCase() === 'table') {
      return currentElement as HTMLTableElement;
    }
    currentElement = currentElement.parentElement;
  }
  return null;
};

type TableExpandable<TData> = {
  getRowCanExpand?: (row: Row<TData>) => boolean;
  onRowClick?: (row: Row<TData>) => void;

  isCurrentRowClickable?: (row: Row<TData>) => boolean;
};

type SortableTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  pagination?: Pagination;
  isDataFetching?: boolean;
  handleSelectTextShow?: boolean;
  pinnedColumns?: {
    left?: string[];
    right?: string[];
  };
  isTableWidthAuto?: boolean;
  manualPagination?: boolean;
  rowSelection?: RowSelection;
  dataEmptyPanel?: React.ReactNode;
  testId?: string;
  enableMultiRowSelection?: boolean;
  visibleColumns?: Record<string, boolean>;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  expandByDefault?: (row: Row<TData>, index: number) => boolean;
  onSortingChange?: (sorting: SortedState) => void;
  sortedState?: SortedState;
  sx?: SystemStyleObject;
  handleExpanded?: (row: Row<TData>) => void;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  expanded?: ExpandedState;
  showBorder?: boolean;
  TheadBackgroundColor?: string;
  footerContent?: React.ReactNode;
  tableCaption?: React.ReactNode;
  rowIdKey?: string;
  isSubTable?: boolean;
  tableMeta?: TableMeta<TData>;
} & TableExpandable<TData> &
  Pick<TableOptions<TData>, 'enableRowSelection'>;

const getCommonPinningStyles = <T,>(
  column: Column<T>,
  isScrollable: boolean,
  isHeader: boolean,
  isTableWidthAuto: boolean,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const backgroundColor = isHeader ? column.columnDef.meta?.headerBackgroundColor || '#F2F2F2' : 'white';

  const size = column.getSize() === 150 ? 'auto' : column.getSize();
  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: isScrollable ? column.getSize() : size,
    maxWidth: isScrollable && isPinned ? column.getSize() : undefined,
    minWidth: isScrollable && isPinned ? column.getSize() : isTableWidthAuto ? column.getSize() : 150,
    backgroundColor,
    whiteSpace: 'break-spaces',
  };
};

const getCommonPinningBoxShadow = <T,>(
  column: Column<T>,
  isScrollable: boolean,
  hasPinnedShadow: { left: boolean; right: boolean },
  isSubTable?: boolean,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  if (!isScrollable) {
    return {};
  }

  const { left, right } = hasPinnedShadow;

  const endBoxShadow = {
    right: '100%',
    width: '30px',
    // if sub table, always show shadow when parent table is scrollable
    background:
      right || isSubTable ? 'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%)' : undefined,
    transition: 'opacity 0.2s ease',
  };

  const startBoxShadow = {
    left: '100%',
    width: '30px',
    // if sub table, always show shadow when parent table is scrollable
    background:
      left || isSubTable ? 'linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%)' : undefined,
    transition: 'opacity 0.2s ease',
  };

  const baseStyle: CSSProperties = {
    content: `""`,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: -1,
  };

  if (isFirstRightPinnedColumn) {
    return {
      ...baseStyle,
      ...endBoxShadow,
    };
  }

  if (isLastLeftPinnedColumn) {
    return {
      ...baseStyle,
      ...startBoxShadow,
    };
  }

  return {};
};

function SortableTable<TData>(props: SortableTableProps<TData>) {
  const {
    sx,
    data,
    columns,
    pagination,
    pinnedColumns,
    dataEmptyPanel,
    testId,
    visibleColumns,
    handleSelectTextShow = true,
    isDataFetching = false,
    manualPagination = true,
    rowSelection = undefined,
    enableMultiRowSelection = true,
    isTableWidthAuto = false,
    isCurrentRowClickable = () => true,
    renderSubComponent,
    getRowCanExpand = () => false,
    onRowClick,
    onSortingChange,
    expandByDefault,
    handleExpanded,
    onExpandedChange,
    expanded,
    sortedState,
    enableRowSelection,
    showBorder = true,
    TheadBackgroundColor = 'gray.50',
    footerContent,
    tableCaption,
    rowIdKey = 'id',
    isSubTable = false,
    tableMeta,
  } = props;

  const { t } = useNextTranslation();

  const sorting = sortedState ? [{ id: sortedState.orderBy || '', desc: sortedState.order === Order.DESC }] : [];
  const [hasSpinner, setHasSpinner] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerScrollWidth, setContainerScrollWidth] = useState(0);
  const parentTableRef = useRef<HTMLTableElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const defaultExpanded = useRef(false);

  const handleScroll = useMemo(
    () =>
      throttle((e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        setScrollLeft(target.scrollLeft);
      }, 250),
    [],
  );

  useEffect(() => {
    return () => {
      handleScroll.cancel();
    };
  }, [handleScroll]);

  const shadowConfig = useMemo(() => {
    const hasLeftShadow = (pinnedColumns?.left?.length ?? 0) <= 0 && scrollLeft > BUFFER_PIXEL;
    const hasRightShadow =
      (pinnedColumns?.right?.length ?? 0) <= 0 &&
      containerRef.current &&
      containerRef.current.scrollWidth - containerWidth - scrollLeft > BUFFER_PIXEL;
    const hasLeftCellShadow = scrollLeft > BUFFER_PIXEL;
    const hasRightCellShadow =
      containerRef.current && containerRef.current.scrollWidth - containerWidth - scrollLeft > BUFFER_PIXEL;

    return {
      container: { left: hasLeftShadow, right: hasRightShadow },
      pinnedCell: { left: hasLeftCellShadow, right: hasRightCellShadow ?? false },
    };
  }, [containerWidth, pinnedColumns, scrollLeft]);

  const tableConfig: TableOptions<TData> = {
    data,
    columns,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: (updater: React.SetStateAction<SortingState>) => {
      if (typeof updater !== 'function') {
        return;
      }
      const newSorting = updater(sorting);
      onSortingChange?.({
        orderBy: newSorting[0]?.id,
        order: typeof newSorting[0]?.desc === 'undefined' ? undefined : newSorting[0]?.desc ? Order.DESC : Order.ASC,
      });
    },
    onPaginationChange: pagination?.setPagination,
    getRowId: (originalRow: TData) => {
      const id = (originalRow as Record<string, unknown>)?.[rowIdKey];
      return id?.toString() || '';
    },
    onRowSelectionChange: rowSelection?.setRowSelection,
    enableMultiRowSelection,
    enableRowSelection,
    pageCount: pagination?.pageCount,
    manualPagination,
    state: {
      rowSelection: rowSelection?.rowSelection,
      pagination: pagination?.state,
      sorting,
      columnVisibility: visibleColumns,
      columnPinning: {
        left: pinnedColumns?.left || [],
        right: pinnedColumns?.right || [],
      },
    },
    meta: tableMeta,
  };
  if (onExpandedChange instanceof Function && expanded) {
    tableConfig.onExpandedChange = onExpandedChange;
    tableConfig.state = { ...tableConfig.state, expanded };
  }
  const table = useReactTable<TData>(tableConfig);

  useEffect(() => {
    clearTimeout(timer.current as NodeJS.Timeout);
    if (isDataFetching) {
      timer.current = setTimeout(() => {
        setHasSpinner(isDataFetching);
      }, LOADING_DELAY_THRESHOLD);
    } else {
      setHasSpinner(false);
    }
  }, [isDataFetching]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          if (entry.target) {
            setContainerWidth(entry.target.clientWidth);
            setContainerScrollWidth(entry.target.scrollWidth);
          }
        }
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // run only once when table data is ready
    if (!defaultExpanded.current && expandByDefault && table.getRowModel().rows.length > 0) {
      table.getRowModel().rows.forEach((row, index) => {
        if (!row.getIsExpanded() && expandByDefault(row, index)) {
          row.toggleExpanded();
        }
      });

      defaultExpanded.current = true;
    }
  }, [table, defaultExpanded, expandByDefault]);

  // 檢查欄位是否需要排序
  const checkCanSortKey = (key: string) => {
    const column = columns.find((col) => col.id === key);
    return column && column.enableSorting ? column.enableSorting : false;
  };

  const shouldShowPagination = data.length > 0 && pagination && pagination.itemCount > 10;

  if (rowSelection && !columns.find((c) => c.id === 'select')) {
    const hasExpander = columns.some((column) => column.id === 'expander');
    columns.splice(hasExpander ? 1 : 0, 0, {
      id: 'select',
      size: !enableMultiRowSelection && handleSelectTextShow ? 60 : 48,
      meta: { align: 'center' },
      header: ({ table: currentTable }) => {
        // make sure to use the actual table behaviour, the parent table variable state might no be the same
        const selectableRows = currentTable.getRowModel().rows.filter((row) => row.getCanSelect());
        return enableMultiRowSelection ? (
          <IndeterminateCheckbox
            {...{
              checked: currentTable.getIsAllPageRowsSelected(),
              // added extra checks to handle case where "Reset Filter" will cause this checkbox to be disabled
              disabled:
                selectableRows.length === 0 &&
                !currentTable.getIsSomePageRowsSelected() &&
                !currentTable.getIsAllPageRowsSelected(),
              indeterminate:
                !currentTable.getIsAllPageRowsSelected() &&
                (currentTable.getIsSomeRowsSelected() || currentTable.getIsSomePageRowsSelected()),
              onChange: currentTable.getToggleAllRowsSelectedHandler(),
              enableMultiRowSelection,
            }}
          />
        ) : (
          handleSelectTextShow && <div>{t('common.select')}</div>
        );
      },
      cell: ({ row }) => (
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
            enableMultiRowSelection,
          }}
        />
      ),
    });
  }

  const isScrollable = isSubTable
    ? (() => {
        if (!parentTableRef.current) {
          parentTableRef.current = findNearestParentTable(containerRef.current);
        }
        if (parentTableRef.current) {
          return parentTableRef.current.getAttribute('data-scrollable') === 'true';
        }
        return false;
      })()
    : containerWidth < containerScrollWidth;

  const isEmptyPanelVisible = data.length <= 0 && dataEmptyPanel;

  return (
    <Stack direction={'column'} gap={4}>
      <Box
        position='relative'
        borderRadius='8px'
        _before={{
          content: isScrollable && shadowConfig.container.left ? '""' : undefined,
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          pointerEvents: 'none',
          width: '1rem',
          zIndex: 1,
          borderRadius: '8px 8px 0 0',
          background: 'linear-gradient(-90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%)',
        }}
        _after={{
          content: isScrollable && shadowConfig.container.right ? '""' : undefined,
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          width: '1rem',
          zIndex: 1,
          borderRadius: '8px 8px 0 0',
          background: 'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%)',
        }}
      >
        <TableContainer
          ref={containerRef}
          border={showBorder ? '1px solid' : undefined}
          position='relative'
          borderColor='gray.200'
          borderRadius={showBorder ? '8px' : undefined}
          overflowY='visible'
          overflowX='auto'
          maxW='100%'
          sx={sx}
          onScroll={handleScroll}
        >
          <Box>
            <Table
              data-testid={testId}
              style={{ tableLayout: isScrollable ? 'auto' : 'fixed' }}
              width={isTableWidthAuto ? 'auto' : undefined}
              minWidth={isScrollable || renderSubComponent ? '100%' : table.getTotalSize()}
              data-scrollable={isScrollable}
            >
              <Thead
                backgroundColor={TheadBackgroundColor}
                sx={{
                  th: {
                    color: 'black',
                    fontWeight: 600,
                    padding: 4,
                    fontSize: 'sm',
                    lineHeight: '1.25rem',
                    whiteSpace: 'break-spaces',
                  },
                }}
              >
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <Tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const align = header.column.columnDef.meta?.align || 'left';
                        const isSortable = checkCanSortKey(header.column.id);
                        return (
                          <Th
                            key={header.id}
                            colSpan={header.colSpan}
                            data-testid={header.id}
                            textAlign={align}
                            className={clsx({
                              [styles.isSticky]: header.column.getIsPinned(),
                            })}
                            _before={getCommonPinningBoxShadow(
                              header.column,
                              isScrollable,
                              shadowConfig.pinnedCell,
                              isSubTable,
                            )}
                            style={{
                              ...getCommonPinningStyles(header.column, isScrollable, true, isTableWidthAuto),
                              width: header.getSize() == 150 ? 'auto' : header.getSize(), // react table default width is 150 and can't override it by auto
                            }}
                          >
                            {!header.isPlaceholder && (
                              <div>
                                <Flex
                                  cursor={isSortable ? 'pointer' : undefined}
                                  alignItems='center'
                                  justifyContent={
                                    align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'
                                  }
                                  onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                                >
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                  <div style={{ marginLeft: '5px', display: 'contents' }}>
                                    {isSortable &&
                                      (sortedState?.orderBy === header.column.id ? (
                                        sortedState.order === Order.ASC ? (
                                          <IconButton
                                            size='sm'
                                            variant='unstyled'
                                            aria-label='sort-down'
                                            icon={<SortDownArrowIcon />}
                                          />
                                        ) : (
                                          <IconButton
                                            size='sm'
                                            variant='unstyled'
                                            aria-label='sort-up'
                                            icon={<SortUpArrowIcon />}
                                          />
                                        )
                                      ) : (
                                        <IconButton
                                          size='sm'
                                          variant='unstyled'
                                          aria-label='sort-default'
                                          icon={<SortUpDownArrowIcon />}
                                        />
                                      ))}
                                  </div>
                                </Flex>
                              </div>
                            )}
                          </Th>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Thead>
              <Tbody
                height={isEmptyPanelVisible && isDataFetching ? 191 : 'auto'}
                sx={{
                  td: {
                    lineHeight: '1.5',
                    borderBottom: 0,
                    borderTop: 'solid 1px',
                    borderColor: 'gray.100',
                    padding: 4,
                    fontSize: 'sm',
                  },
                }}
              >
                {table.getRowModel().rows.map((row, index) => {
                  const hasRowClickCallback = typeof onRowClick === 'function';
                  const isRowExpandable = typeof getRowCanExpand === 'function' && getRowCanExpand(row) === true;
                  const getIsCurrentRowClickable = () => {
                    if (typeof isCurrentRowClickable === 'function') {
                      return isCurrentRowClickable(row);
                    }

                    if (typeof isCurrentRowClickable === 'boolean') {
                      return isCurrentRowClickable;
                    }
                    return true;
                  };

                  typeof isCurrentRowClickable === 'function' ? isCurrentRowClickable(row) : isCurrentRowClickable;

                  const isRowClickable = isRowExpandable || (hasRowClickCallback && getIsCurrentRowClickable());

                  const handleRowClick = () => {
                    if (hasRowClickCallback) {
                      onRowClick(row);
                      return;
                    }

                    if (typeof getRowCanExpand === 'function' && getRowCanExpand(row) === true) {
                      const toggleExpander = row.getToggleExpandedHandler();
                      toggleExpander();
                      handleExpanded?.(row);
                    }
                  };

                  return (
                    <Fragment key={index}>
                      <Tr onClick={handleRowClick} _hover={{ cursor: isRowClickable ? 'pointer' : 'default' }}>
                        {row.getVisibleCells().map((cell) => (
                          <Td
                            id={cell.id}
                            key={cell.id}
                            data-testid={cell.column.id}
                            className={clsx({
                              [styles.isSticky]: cell.column.getIsPinned(),
                            })}
                            _before={getCommonPinningBoxShadow(
                              cell.column,
                              isScrollable,
                              shadowConfig.pinnedCell,
                              isSubTable,
                            )}
                            style={getCommonPinningStyles(cell.column, isScrollable, false, isTableWidthAuto)}
                          >
                            <Box textAlign={cell.column.columnDef.meta?.align || 'left'}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Box>
                            {cell.column.getIsPinned() && (
                              <span className={`${styles.hover_shadow} hover_shadow`}></span>
                            )}
                          </Td>
                        ))}
                      </Tr>
                      {row.getIsExpanded() && typeof renderSubComponent === 'function' && renderSubComponent({ row })}
                    </Fragment>
                  );
                })}
              </Tbody>
              {tableCaption && (
                <TableCaption mt={0} borderTop={'1px solid'} borderColor='gray.100' textAlign={'left'}>
                  {tableCaption}
                </TableCaption>
              )}
            </Table>
            {hasSpinner && <FetchingDataPanel />}
          </Box>
          {isEmptyPanelVisible && !isDataFetching && (
            <Box as='div' px='4' py='8' position='sticky' left='0' textAlign='center'>
              {dataEmptyPanel}
            </Box>
          )}
        </TableContainer>
      </Box>
      {footerContent}
      {shouldShowPagination && <TablePagination pagination={pagination} onPaginationChange={table.setPagination} />}
    </Stack>
  );
}

const FetchingDataPanel = () => {
  const { t } = useNextTranslation();
  return (
    <Flex
      justifyContent='center'
      alignItems='center'
      style={{
        backgroundColor: 'black',
        opacity: 0.48,
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <Stack gap={2} alignItems='center'>
        <Spinner thickness='4px' speed='.65s' emptyColor='white' color='gray.400' size='xl' />
        <Text fontSize='1rem' fontWeight='400' lineHeight={1.5} color='white'>
          {t('common.loading')}
        </Text>
      </Stack>
    </Flex>
  );
};

export default SortableTable;
